import React from 'react';
import { Container, Box, Typography, Chip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
  color: 'white',
  padding: '64px 0',
  textAlign: 'center',
});

const Section = ({ title, children, id }) => (
  <Box id={id} sx={{ mb: 4, scrollMarginTop: '80px' }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>{children}</Typography>
);

function TermsPage() {
  return (
    <Box>
      <SEO title="Terms of Service" description="Cyber Wise Corp terms of service for assessment bookings." />
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="Legal" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Terms of Service
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Last updated: April 2026
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <P>
          These Terms of Service ("Terms") govern your use of the Cyber Wise Corp ("CWC," "we," "us," or "our") website at cyberwisecorp.com and the cybersecurity assessment services we provide. By using our website or booking an assessment, you agree to these Terms.
        </P>

        <Divider sx={{ my: 4 }} />

        <Section title="1. Services Overview">
          <P>
            CWC provides cybersecurity assessment services for homes and small businesses. Assessments are performed by vetted college cybersecurity interns and reviewed by CISSP-certified cybersecurity experts. Our services include on-site security evaluations, personalized consultations, and expert-reviewed security reports.
          </P>
        </Section>

        <Section title="2. Booking and Payment">
          <P>
            Home assessments are $249.99 and include a 1-hour on-site evaluation. Small business assessments (10 or fewer employees) are $599.99 and include a 2-hour on-site evaluation. Payment is required at the time of booking and is processed securely through Stripe.
          </P>
          <P>
            By booking an assessment, you authorize CWC to charge the applicable fee to your payment method. Prices are subject to change, but any price in effect at the time of your booking will be honored.
          </P>
        </Section>

        <Section title="3. Cancellation and Refund Policy">
          <P>
            You may cancel or reschedule your assessment free of charge with at least 24 hours notice before the scheduled assessment time. Cancellations made with 24 or more hours notice will receive a full refund.
          </P>
          <P>
            Cancellations made with less than 24 hours notice may be subject to the full assessment fee. If CWC cancels an assessment for any reason, you will receive a full refund regardless of timing.
          </P>
        </Section>

        <Section id="sms" title="4. SMS Communications & Consent (Cyber Wise Corp Notifications)">
          <P>
            <strong>Program name:</strong> Cyber Wise Corp Notifications.
          </P>
          <P>
            By providing your mobile phone number on our website (when booking an assessment at cyberwisecorp.com/book, applying as a college intern at students.cyberwisecorp.com, or being provisioned as a platform administrator), you expressly consent to receive operational text messages (SMS) from Cyber Wise Corp related to your account and bookings.
          </P>
          <P>
            <strong>Message types include:</strong> booking confirmations, appointment reminders (typically 24 hours and 1 hour before your scheduled assessment), schedule changes, urgent unfilled-booking alerts to administrators, new-job alerts to interns, and other transactional messages directly tied to your use of the service. We do not send marketing or promotional SMS through this program.
          </P>
          <P>
            <strong>Message frequency:</strong> Recurring, but variable based on your activity. Most users receive fewer than 10 messages per month. Administrators may receive more during periods of high booking volume.
          </P>
          <P>
            <strong>Message and data rates may apply.</strong> Standard message and data rates from your wireless carrier apply to all messages sent and received. Cyber Wise Corp is not responsible for any charges from your wireless carrier.
          </P>
          <P>
            <strong>To opt out at any time, reply STOP</strong> to any text message you receive from us. You will receive a single confirmation message and no further SMS will be sent. To rejoin, reply <strong>START</strong>. For help, reply <strong>HELP</strong> or contact us through our contact form at cyberwisecorp.com/contact.
          </P>
          <P>
            Carriers (including but not limited to AT&amp;T, T-Mobile, Verizon, and their affiliates) are not liable for delayed or undelivered messages. Message delivery is subject to the availability and performance of the recipient's wireless network.
          </P>
          <P>
            We do not share your mobile phone number or SMS opt-in status with third parties or affiliates for marketing purposes. Your phone number and consent record are stored securely and used only to deliver the messages described above. See our <a href="/privacy" style={{ color: '#7c3aed' }}>Privacy Policy</a> for full details on data handling.
          </P>
        </Section>

        <Section title="5. Assessment Scope and Limitations">
          <P>
            Our assessments evaluate your cybersecurity posture by examining network configuration, device security, data protection practices, and user awareness. The assessment is a point-in-time evaluation and does not constitute ongoing monitoring or protection.
          </P>
          <P>
            CWC does not guarantee that following our recommendations will prevent all security incidents. Cybersecurity is an ongoing process and no assessment can identify every possible vulnerability. Our reports provide recommendations to reduce your risk, not eliminate it entirely.
          </P>
          <P>
            Our interns will not access, view, or copy any personal files, emails, photos, financial records, or other private content during the assessment. The assessment is limited to evaluating security configurations and practices.
          </P>
        </Section>

        <Section title="6. Client Responsibilities">
          <P>During your assessment, you agree to:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Have a person 18 years or older present at the assessment location for the full duration</li>
            <li>Provide WiFi network access for the intern to evaluate your network security</li>
            <li>Ensure devices to be assessed are powered on and accessible</li>
            <li>Provide accurate contact information and address for the assessment location</li>
            <li>Treat our interns with respect and professionalism</li>
          </Box>
          <P>
            CWC reserves the right to terminate an assessment and issue a full refund if the intern feels unsafe, is subjected to harassment, or if the assessment environment presents unreasonable risk.
          </P>
        </Section>

        <Section title="7. Intern Conduct">
          <P>
            All CWC interns are bound by our Intern Code of Conduct, which requires professional behavior, strict privacy protocols, and adherence to our assessment framework. If you have concerns about an intern's conduct during your assessment, please contact us immediately through our contact form.
          </P>
        </Section>

        <Section title="8. Intellectual Property">
          <P>
            The CWC assessment framework, scoring methodology, report templates, and website content are the intellectual property of Cyber Wise Corp. Your assessment report is provided for your personal or business use only and may not be resold, redistributed, or used for commercial purposes without our written consent.
          </P>
        </Section>

        <Section title="9. Limitation of Liability">
          <P>
            To the maximum extent permitted by law, CWC, its officers, employees, interns, and expert reviewers shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to your use of our services, including but not limited to any security incident that occurs before, during, or after an assessment.
          </P>
          <P>
            Our total liability for any claim arising from our services shall not exceed the amount you paid for the specific assessment giving rise to the claim.
          </P>
        </Section>

        <Section title="10. Indemnification">
          <P>
            You agree to indemnify and hold harmless CWC, its officers, employees, interns, and expert reviewers from any claims, damages, losses, or expenses arising from your breach of these Terms, your misuse of our services, or your violation of any applicable law.
          </P>
        </Section>

        <Section title="11. Dispute Resolution">
          <P>
            Any disputes arising from these Terms or our services shall be resolved through binding arbitration in Maricopa County, Arizona, in accordance with the rules of the American Arbitration Association. You agree to waive any right to a jury trial or to participate in a class action lawsuit.
          </P>
        </Section>

        <Section title="12. Governing Law">
          <P>
            These Terms are governed by the laws of the State of Arizona without regard to conflict of law principles. Any legal proceedings not subject to arbitration shall be brought in the state or federal courts located in Maricopa County, Arizona.
          </P>
        </Section>

        <Section title="13. Changes to These Terms">
          <P>
            We may update these Terms from time to time. When we do, we will revise the "Last updated" date at the top of this page. Continued use of our services after changes are posted constitutes your acceptance of the revised Terms.
          </P>
        </Section>

        <Section title="14. Contact">
          <P>
            If you have questions about these Terms, please contact us through the contact form on our website. Cyber Wise Corp is based in Glendale, Arizona.
          </P>
        </Section>
      </Container>
    </Box>
  );
}

export default TermsPage;
